import React, { ChangeEvent, FC } from 'react';

import { useFormik } from 'formik';
import styled from 'styled-components';
import * as yup from 'yup';

import { Button, Stack, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formMsg, notifyMsg } from '../../configs';
import { useCreateKvs } from '../../api-http/kvs';
import { AlertBar } from '../../components/AlertBar';
import { useSnack } from '../../util/useSnack';
import { formatInput } from '../../util/validations';

const Container = styled.div`
  display: flex;
  padding-top: 18px;
  padding-bottom: 18px;
  flex-direction: column;
`;

interface KvsFormProps {
  onFinished: () => void;
  onRefetchData: () => void;
}

const validationSchema = yup.object({
  groupName: yup
    .string()
    .required(formMsg.GROUP_NAME_REQUIRED)
    .matches(/^[0-9a-z_]+$/, formMsg.INVALID_GROUP_NAME),
  description: yup.string().required(formMsg.KVS_DESCRIPTION_REQUIRED),
});

export const KvsForm: FC<KvsFormProps> = ({ onFinished, onRefetchData }) => {
  const navigate = useNavigate();
  const { showErrSnack, showSuccessSnack } = useSnack();

  const { isLoading, mutate, error } = useCreateKvs({
    onSuccess: (createdGroup) => {
      showSuccessSnack(notifyMsg.KVS_GROUP_CREATE_SUCCESS);
      onRefetchData();
      onFinished();
      navigate(`/kvs/${createdGroup._id}`, {
        state: { groupName: createdGroup.name },
      });
    },
    onError: (error) => {
      showErrSnack(error || notifyMsg.KVS_GROUP_CREATE_FAILED);
    },
  });

  const formik = useFormik({
    initialValues: {
      groupName: '',
      description: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const { groupName, description } = values;
      mutate({
        name: groupName,
        description: description,
        data: [],
      });
    },
  });

  return (
    <Container>
      <AlertBar
        style={{ marginBottom: '10px' }}
        severity="error"
        msg={error?.msg}
      />
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            id="groupName"
            name="groupName"
            label="Group name"
            value={formik.values.groupName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              formatInput(e, 'groupName', formik)
            }
            error={formik.touched.groupName && Boolean(formik.errors.groupName)}
            helperText={formik.touched.groupName && formik.errors.groupName}
          />
          <TextField
            size="small"
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />

          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={isLoading}
            sx={{ minHeight: 40 }}
          >
            Create group
          </Button>
        </Stack>
      </form>
    </Container>
  );
};
